import { useState, useCallback, useMemo, useEffect, type KeyboardEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import {
  Search,
  Plus,
  ChevronDown,
  ChevronUp,
  Trash2,
  Pencil,
  ArrowUp,
  ArrowDown,
  Check,
  X,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import {
  useQuestions,
  useQuestion,
  useCreateQuestion,
  useUpdateQuestion,
  useDeleteQuestion,
  useCreateAnswer,
  useUpdateAnswer,
  useDeleteAnswer,
  useDiseaseAssociations,
} from '@/hooks/use-questions';
import {
  useInformationalTexts,
  useCreateInformationalText,
  useUpdateInformationalText,
  useDeleteInformationalText,
  useInfoCategories,
} from '@/hooks/use-informational-texts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { canCreateQuestion, canManageQuestion } from '@/types/workflow';
import type {
  Question,
  QuestionAnswer,
  QuestionFilters,
  InformationalText,
  InfoTextFilters,
} from '@/types/question';
import type { AlertLevel } from '@/types/template';

const PAGE_SIZE = 25;

const ALERT_COLORS: Record<AlertLevel, string> = {
  0: 'bg-green-100 text-green-700 border-green-200',
  1: 'bg-green-50 text-green-600 border-green-100',
  2: 'bg-honey-100 text-honey-800 border-honey-200',
  3: 'bg-red-100 text-red-700 border-red-200',
  4: 'bg-red-200 text-red-800 border-red-300',
};

export default function QuestionBank() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const { data: diseases = [] } = useDiseaseAssociations();

  const filters: QuestionFilters = useMemo(() => ({
    keyword: searchParams.get('keyword') || undefined,
    diseaseAssociation: searchParams.get('disease') || undefined,
    // SE-API-18: page/pageSize (page is 1-based).
    page: Number(searchParams.get('page') || 1),
    pageSize: PAGE_SIZE,
  }), [searchParams]);

  const currentPage = Number(searchParams.get('page') || 1);
  const { data, isLoading, error } = useQuestions(filters);
  const pageCount = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  // Debounced search
  const [searchInput, setSearchInput] = useState(filters.keyword ?? '');
  const searchTimeoutRef = useState<ReturnType<typeof setTimeout> | null>(null);

  const updateFilter = useCallback(
    (key: string, value: string | undefined) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        next.delete('page');
        return next;
      });
    },
    [setSearchParams],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (searchTimeoutRef[0]) clearTimeout(searchTimeoutRef[0]);
      searchTimeoutRef[0] = setTimeout(() => {
        updateFilter('keyword', value || undefined);
      }, 300);
    },
    [updateFilter, searchTimeoutRef],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (page > 1) next.set('page', String(page));
        else next.delete('page');
        return next;
      });
    },
    [setSearchParams],
  );

  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') || 'questions',
  );

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-carbon-900">Content Bank</h2>
          <p className="text-sm text-carbon-500">
            Browse and manage survey questions and informational text
          </p>
        </div>
        {activeTab === 'questions' && user && canCreateQuestion(user.role) && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            New Question
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="questions">Questions</TabsTrigger>
          <TabsTrigger value="informational">Informational Text</TabsTrigger>
        </TabsList>

        <TabsContent value="questions">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-carbon-400" />
              <Input
                placeholder="Search questions..."
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select
              value={filters.diseaseAssociation ?? '__none__'}
              onValueChange={(v) =>
                updateFilter('disease', v === '__none__' ? undefined : v)
              }
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Diseases" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">All Diseases</SelectItem>
                {diseases.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {(filters.keyword || filters.diseaseAssociation) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchParams({});
                  setSearchInput('');
                }}
              >
                Clear filters
              </Button>
            )}
          </div>

          {/* Question grid */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-40" />
              ))}
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-600">
              Failed to load questions. Check your API connection.
            </div>
          ) : data?.entries.length === 0 ? (
            <div className="p-16 text-center text-carbon-500">
              No questions found
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data?.entries.map((q) => (
                  <QuestionCard key={q.question_id} question={q} />
                ))}
              </div>

              <div className="flex items-center justify-between mt-6">
                <p className="text-sm text-carbon-500">
                  {data?.total
                    ? `Showing ${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, data.total)} of ${data.total}`
                    : ''}
                </p>
                <Pagination
                  page={currentPage}
                  pageCount={pageCount}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="informational">
          <InformationalTextTab />
        </TabsContent>
      </Tabs>

      {/* Create question dialog */}
      {createOpen && (
        <CreateQuestionDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onSuccess={() => toast({ title: 'Question created', variant: 'success' })}
        />
      )}
    </div>
  );
}

// -- Question Card --

function QuestionCard({ question }: { question: Question }) {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const canManage = !!user && canManageQuestion(user.role);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-sm">
            <span className="font-bold text-purple-600">Q-{question.question_id}</span>
          </CardTitle>
          <div className="flex items-center gap-1">
            {question.disease_association && (
              <Badge variant="outline" className="shrink-0">
                {question.disease_association}
              </Badge>
            )}
            {canManage && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Edit question"
                  className="h-7 w-7 shrink-0 text-carbon-500 hover:text-purple-600"
                  onClick={() => setEditOpen(true)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Delete question"
                  className="h-7 w-7 shrink-0 text-carbon-500 hover:text-red-600"
                  onClick={() => setDeleteOpen(true)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-carbon-800 leading-relaxed">
          {question.question_text}
        </p>

        {(question.answers?.length ?? 0) > 0 && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs font-medium text-purple-500 hover:text-purple-700"
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-3 w-3" /> Hide Answers
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3" /> Show Answers ({question.answers?.length ?? 0})
                </>
              )}
            </button>

            {expanded && (
              <div className="mt-2 space-y-1.5">
                {(question.answers ?? [])
                  .slice()
                  .sort((a, b) => a.display_order - b.display_order)
                  .map((a) => (
                    <div
                      key={a.question_answer_id}
                      className={`flex items-center gap-2 rounded-ds-004 border px-3 py-1.5 text-xs ${ALERT_COLORS[a.alert_level]}`}
                    >
                      <span className="font-medium">L{a.alert_level}</span>
                      <span>{a.answer_text}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </CardContent>

      {editOpen && (
        <EditQuestionDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          questionId={question.question_id}
        />
      )}
      {deleteOpen && (
        <DeleteQuestionDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          questionId={question.question_id}
          questionText={question.question_text}
        />
      )}
    </Card>
  );
}

// -- Create Question Dialog --

interface CreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

function CreateQuestionDialog({ open, onOpenChange, onSuccess }: CreateDialogProps) {
  const { data: diseases = [] } = useDiseaseAssociations();
  const createMutation = useCreateQuestion();
  const [questionText, setQuestionText] = useState('');
  const [disease, setDisease] = useState('__none__');
  const [answers, setAnswers] = useState<{ text: string; level: AlertLevel }[]>([
    { text: '', level: 0 },
  ]);

  const addAnswer = () => setAnswers((prev) => [...prev, { text: '', level: 0 }]);
  const removeAnswer = (idx: number) => setAnswers((prev) => prev.filter((_, i) => i !== idx));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await createMutation.mutateAsync({
        questionText,
        diseaseAssociation: disease === '__none__' ? undefined : disease,
        answers: answers
          .filter((a) => a.text.trim())
          .map((a, i) => ({
            answerText: a.text,
            alertLevel: a.level,
            displayOrder: i + 1,
          })),
      });
      onSuccess();
      onOpenChange(false);
    } catch {
      // Error handled by mutation
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Question</DialogTitle>
          <DialogDescription>Add a new question to the question bank.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <label className="block text-sm font-medium text-carbon-700 mb-1">
              Question Text
            </label>
            <textarea
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              className="flex w-full rounded-ds-004 border border-silver-300 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 min-h-[80px]"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-carbon-700 mb-1">
              Disease Association
            </label>
            <Select value={disease} onValueChange={setDisease}>
              <SelectTrigger>
                <SelectValue placeholder="None (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {diseases.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-carbon-700">Answers</label>
              <Button type="button" variant="ghost" size="sm" onClick={addAnswer}>
                <Plus className="h-3 w-3 mr-1" /> Add Answer
              </Button>
            </div>
            <div className="space-y-2">
              {answers.map((a, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    placeholder={`Answer ${idx + 1}`}
                    value={a.text}
                    onChange={(e) => {
                      const next = [...answers];
                      next[idx] = { ...next[idx], text: e.target.value };
                      setAnswers(next);
                    }}
                    className="flex-1"
                  />
                  <Select
                    value={String(a.level)}
                    onValueChange={(v) => {
                      const next = [...answers];
                      next[idx] = { ...next[idx], level: Number(v) as AlertLevel };
                      setAnswers(next);
                    }}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {([0, 1, 2, 3, 4] as AlertLevel[]).map((l) => (
                        <SelectItem key={l} value={String(l)}>L{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {answers.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0 text-red-500 hover:text-red-700"
                      onClick={() => removeAnswer(idx)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Question'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// CMS-02 — Edit Question Dialog
// ---------------------------------------------------------------------------
// Fetches full question detail (list endpoint omits answers per
// types/question.ts comment) and lets a TCM Admin edit:
//   - Question text + disease association  → PATCH /questions/{id}
//   - Per-answer add / edit / delete / reorder  → POST/PATCH/DELETE
//     on /questions/{id}/answers
// Each answer action is its own immediate mutation; question-level
// changes are batched into a single Save action on the dialog.

interface EditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  questionId: number;
}

function EditQuestionDialog({ open, onOpenChange, questionId }: EditDialogProps) {
  const { toast } = useToast();
  const { data: diseases = [] } = useDiseaseAssociations();
  const { data: detail, isLoading } = useQuestion(questionId);
  const updateMutation = useUpdateQuestion();

  const [questionText, setQuestionText] = useState('');
  const [disease, setDisease] = useState('__none__');

  // Seed local edit state once the detail loads.
  useEffect(() => {
    if (detail) {
      setQuestionText(detail.question_text);
      setDisease(detail.disease_association ?? '__none__');
    }
  }, [detail]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!detail) return;

    const changes: { questionText?: string; diseaseAssociation?: string | null } = {};
    if (questionText !== detail.question_text) {
      changes.questionText = questionText;
    }
    const nextDisease = disease === '__none__' ? null : disease;
    if (nextDisease !== detail.disease_association) {
      changes.diseaseAssociation = nextDisease;
    }
    if (Object.keys(changes).length === 0) {
      onOpenChange(false);
      return;
    }
    try {
      await updateMutation.mutateAsync({ questionId, data: changes });
      toast({ title: 'Question updated', variant: 'success' });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Update failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Question</DialogTitle>
          <DialogDescription>
            Update the question text, disease association, and answers.
          </DialogDescription>
        </DialogHeader>

        {isLoading || !detail ? (
          <div className="space-y-3 mt-2">
            <Skeleton className="h-20" />
            <Skeleton className="h-10" />
            <Skeleton className="h-32" />
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-medium text-carbon-700 mb-1">
                Question Text
              </label>
              <textarea
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                className="flex w-full rounded-ds-004 border border-silver-300 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 min-h-[80px]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-carbon-700 mb-1">
                Disease Association
              </label>
              <Select value={disease} onValueChange={setDisease}>
                <SelectTrigger>
                  <SelectValue placeholder="None (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {diseases.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <AnswersEditor
              questionId={questionId}
              answers={detail.answers ?? []}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving...' : 'Save Question'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// CMS-02 — Answers editor (used inside EditQuestionDialog)
// ---------------------------------------------------------------------------

interface AnswersEditorProps {
  questionId: number;
  answers: QuestionAnswer[];
}

function AnswersEditor({ questionId, answers }: AnswersEditorProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createMutation = useCreateAnswer();
  const updateMutation = useUpdateAnswer();
  const deleteMutation = useDeleteAnswer();

  const sorted = [...answers].sort((a, b) => a.display_order - b.display_order);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  // Inline delete confirmation — an answer row lives inside the Edit Question
  // modal, so we confirm in-row rather than stacking a second modal.
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  async function reorder(
    answer: QuestionAnswer,
    direction: 'up' | 'down',
  ) {
    const idx = sorted.findIndex((a) => a.question_answer_id === answer.question_answer_id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const other = sorted[swapIdx];

    // Two-step swap via a temporary out-of-band display_order to avoid the
    // UNIQUE(question_id, display_order) collision. Use a sentinel value
    // larger than any existing order.
    const sentinel = Math.max(...sorted.map((a) => a.display_order)) + 1000;
    try {
      await updateMutation.mutateAsync({
        questionId,
        answerId: answer.question_answer_id,
        data: { displayOrder: sentinel },
      });
      await updateMutation.mutateAsync({
        questionId,
        answerId: other.question_answer_id,
        data: { displayOrder: answer.display_order },
      });
      await updateMutation.mutateAsync({
        questionId,
        answerId: answer.question_answer_id,
        data: { displayOrder: other.display_order },
      });
    } catch (err) {
      toast({
        title: 'Reorder failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  }

  async function handleDelete(answer: QuestionAnswer) {
    try {
      await deleteMutation.mutateAsync({
        questionId,
        answerId: answer.question_answer_id,
      });
      toast({ title: 'Answer deleted', variant: 'success' });
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-carbon-700">Answers</label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setAdding(true)}
          disabled={adding}
        >
          <Plus className="h-3 w-3 mr-1" /> Add Answer
        </Button>
      </div>

      <div className="space-y-2">
        {sorted.length === 0 && !adding && (
          <p className="text-xs text-carbon-500">No answers yet.</p>
        )}

        {sorted.map((a, idx) =>
          editingId === a.question_answer_id ? (
            <AnswerEditRow
              key={a.question_answer_id}
              answer={a}
              onCancel={() => setEditingId(null)}
              onSave={async (data) => {
                try {
                  await updateMutation.mutateAsync({
                    questionId,
                    answerId: a.question_answer_id,
                    data,
                  });
                  // Wait for both list + detail queries to refetch so the
                  // display row and question card show updated text immediately.
                  await queryClient.invalidateQueries({
                    queryKey: ['questions'],
                  });
                  toast({ title: 'Answer updated', variant: 'success' });
                  setEditingId(null);
                } catch (err) {
                  toast({
                    title: 'Update failed',
                    description: err instanceof Error ? err.message : String(err),
                    variant: 'destructive',
                  });
                }
              }}
            />
          ) : (
            <div
              key={a.question_answer_id}
              className={`flex items-center gap-2 rounded-ds-004 border px-2 py-1.5 text-xs ${ALERT_COLORS[a.alert_level]}`}
            >
              <span className="font-medium shrink-0">L{a.alert_level}</span>
              <span className="flex-1">{a.answer_text}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Move up"
                className="h-6 w-6 shrink-0"
                disabled={idx === 0}
                onClick={() => reorder(a, 'up')}
              >
                <ArrowUp className="h-3 w-3" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Move down"
                className="h-6 w-6 shrink-0"
                disabled={idx === sorted.length - 1}
                onClick={() => reorder(a, 'down')}
              >
                <ArrowDown className="h-3 w-3" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Edit answer"
                className="h-6 w-6 shrink-0"
                onClick={() => setEditingId(a.question_answer_id)}
              >
                <Pencil className="h-3 w-3" />
              </Button>
              {confirmDeleteId === a.question_answer_id ? (
                <>
                  <span className="shrink-0 text-[11px] font-medium">
                    Delete?
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Confirm delete answer"
                    className="h-6 w-6 shrink-0 text-red-600 hover:text-red-700"
                    onClick={() => {
                      setConfirmDeleteId(null);
                      void handleDelete(a);
                    }}
                  >
                    <Check className="h-3 w-3" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Cancel delete answer"
                    className="h-6 w-6 shrink-0"
                    onClick={() => setConfirmDeleteId(null)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Delete answer"
                  className="h-6 w-6 shrink-0 text-red-500 hover:text-red-700"
                  onClick={() => setConfirmDeleteId(a.question_answer_id)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          ),
        )}

        {adding && (
          <AnswerEditRow
            answer={null}
            onCancel={() => setAdding(false)}
            onSave={async (data) => {
              if (!data.answerText || data.alertLevel === undefined) return;
              try {
                await createMutation.mutateAsync({
                  questionId,
                  data: {
                    answerText: data.answerText,
                    alertLevel: data.alertLevel,
                    displayOrder: sorted.length + 1,
                  },
                });
                toast({ title: 'Answer added', variant: 'success' });
                setAdding(false);
              } catch (err) {
                toast({
                  title: 'Add failed',
                  description: err instanceof Error ? err.message : String(err),
                  variant: 'destructive',
                });
              }
            }}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CMS-02 — Single-row inline editor for an Answer (used by AnswersEditor)
// ---------------------------------------------------------------------------

interface AnswerEditRowProps {
  answer: QuestionAnswer | null; // null = add mode
  onSave: (data: { answerText?: string; alertLevel?: AlertLevel }) => void | Promise<void>;
  onCancel: () => void;
}

function AnswerEditRow({ answer, onSave, onCancel }: AnswerEditRowProps) {
  const [text, setText] = useState(answer?.answer_text ?? '');
  const [level, setLevel] = useState<AlertLevel>(answer?.alert_level ?? 0);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!text.trim()) return;
    setSaving(true);
    try {
      if (answer) {
        const changes: { answerText?: string; alertLevel?: AlertLevel } = {};
        if (text !== answer.answer_text) changes.answerText = text;
        if (level !== answer.alert_level) changes.alertLevel = level;
        if (Object.keys(changes).length === 0) {
          onCancel();
          return;
        }
        await onSave(changes);
      } else {
        await onSave({ answerText: text, alertLevel: level });
      }
    } finally {
      setSaving(false);
    }
  }

  /** Enter saves the answer; prevents the parent form from submitting. */
  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  }

  return (
    <div className="flex items-center gap-2 rounded-ds-004 border border-purple-200 bg-purple-50/50 px-2 py-1.5">
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Answer text"
        className="flex-1 h-8 text-xs"
        autoFocus
      />
      <Select value={String(level)} onValueChange={(v) => setLevel(Number(v) as AlertLevel)}>
        <SelectTrigger className="w-16 h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {([0, 1, 2, 3, 4] as AlertLevel[]).map((l) => (
            <SelectItem key={l} value={String(l)}>L{l}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Save answer"
        className="h-6 w-6 shrink-0 text-green-600 hover:text-green-700"
        disabled={saving || !text.trim()}
        onClick={handleSave}
      >
        <Check className="h-3 w-3" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Cancel"
        className="h-6 w-6 shrink-0 text-carbon-500"
        onClick={onCancel}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CMS-02 — Delete Question Confirmation Dialog
// ---------------------------------------------------------------------------
// The backend refuses delete (409) when the question is referenced by a
// template currently at status='live'. The dialog surfaces that conflict
// as a clear error toast instead of a generic "failed."

interface DeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  questionId: number;
  questionText: string;
}

function DeleteQuestionDialog({
  open,
  onOpenChange,
  questionId,
  questionText,
}: DeleteDialogProps) {
  const { toast } = useToast();
  const deleteMutation = useDeleteQuestion();

  async function handleConfirm() {
    try {
      await deleteMutation.mutateAsync(questionId);
      toast({ title: 'Question deleted', variant: 'success' });
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const looksLikeConflict =
        message.toLowerCase().includes('live template') ||
        message.toLowerCase().includes('referenced');
      toast({
        title: looksLikeConflict ? 'Cannot delete — referenced by live template' : 'Delete failed',
        description: message,
        variant: 'destructive',
      });
      onOpenChange(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete question?</DialogTitle>
          <DialogDescription>
            This will hide the question from the bank. It cannot be deleted if any
            currently-live template references it.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-ds-004 border border-silver-200 bg-silver-50 p-3 text-sm text-carbon-700">
          {questionText}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-red-600 hover:bg-red-700"
            disabled={deleteMutation.isPending}
            onClick={handleConfirm}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Delete confirmation for an informational text. Mirrors DeleteQuestionDialog.
// The conflict-message handling is defensive — the backend live-template guard
// for info-texts is a follow-up (PR B); until then a 409 won't occur, but the
// graceful branch is harmless and ready for it.
// ---------------------------------------------------------------------------

interface DeleteInfoTextDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: InformationalText;
}

function DeleteInfoTextDialog({
  open,
  onOpenChange,
  item,
}: DeleteInfoTextDialogProps) {
  const { toast } = useToast();
  const deleteMutation = useDeleteInformationalText();

  async function handleConfirm() {
    try {
      await deleteMutation.mutateAsync(item.informational_text_id);
      toast({ title: 'Informational text deleted', variant: 'success' });
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const looksLikeConflict =
        message.toLowerCase().includes('live template') ||
        message.toLowerCase().includes('referenced');
      toast({
        title: looksLikeConflict
          ? 'Cannot delete — referenced by live template'
          : 'Delete failed',
        description: message,
        variant: 'destructive',
      });
      onOpenChange(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete informational text?</DialogTitle>
          <DialogDescription>
            This will hide the informational text from the bank.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-ds-004 border border-silver-200 bg-silver-50 p-3 text-sm text-carbon-700 whitespace-pre-wrap">
          {item.body}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-red-600 hover:bg-red-700"
            disabled={deleteMutation.isPending}
            onClick={handleConfirm}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Informational Text Tab
// ===========================================================================

const INFO_PAGE_SIZE = 25;

function InformationalTextTab() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: categories = [] } = useInfoCategories();
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<InformationalText | null>(null);
  const [deleteItem, setDeleteItem] = useState<InformationalText | null>(null);

  const [searchInput, setSearchInput] = useState(
    searchParams.get('infoKeyword') ?? '',
  );
  const searchTimeoutRef = useState<ReturnType<typeof setTimeout> | null>(null);

  const filters: InfoTextFilters = useMemo(
    () => ({
      keyword: searchParams.get('infoKeyword') || undefined,
      category: searchParams.get('infoCategory') || undefined,
      // SE-API-18: page/pageSize (page is 1-based).
      page: Number(searchParams.get('infoPage') || 1),
      pageSize: INFO_PAGE_SIZE,
    }),
    [searchParams],
  );

  const currentPage = Number(searchParams.get('infoPage') || 1);
  const { data, isLoading, error } = useInformationalTexts(filters);
  const pageCount = data ? Math.ceil(data.total / INFO_PAGE_SIZE) : 0;

  const updateInfoFilter = useCallback(
    (key: string, value: string | undefined) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        next.delete('infoPage');
        return next;
      });
    },
    [setSearchParams],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (searchTimeoutRef[0]) clearTimeout(searchTimeoutRef[0]);
      searchTimeoutRef[0] = setTimeout(() => {
        updateInfoFilter('infoKeyword', value || undefined);
      }, 300);
    },
    [updateInfoFilter, searchTimeoutRef],
  );

  const canManage = !!user && canManageQuestion(user.role);

  return (
    <div className="space-y-6">
      {/* Filters + create button */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-carbon-400" />
          <Input
            placeholder="Search informational texts..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select
          value={filters.category ?? '__all__'}
          onValueChange={(v) =>
            updateInfoFilter('infoCategory', v === '__all__' ? undefined : v)
          }
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(filters.keyword || filters.category) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete('infoKeyword');
                next.delete('infoCategory');
                next.delete('infoPage');
                return next;
              });
              setSearchInput('');
            }}
          >
            Clear filters
          </Button>
        )}

        {canManage && (
          <Button
            size="sm"
            className="ml-auto"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="h-4 w-4 mr-1" /> New Text
          </Button>
        )}
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : error ? (
        <div className="p-8 text-center text-red-600">
          Failed to load informational texts. Check your API connection.
        </div>
      ) : data?.entries.length === 0 ? (
        <div className="p-16 text-center text-carbon-500">
          No informational texts found
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data?.entries.map((item) => (
              <Card key={item.informational_text_id}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-sm">
                      <span className="font-bold text-purple-600">
                        I-{item.informational_text_id}
                      </span>
                    </CardTitle>
                    <div className="flex items-center gap-1">
                      {item.category && (
                        <Badge variant="outline" className="shrink-0">
                          {item.category}
                        </Badge>
                      )}
                      {canManage && (
                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label="Edit"
                            className="h-7 w-7 shrink-0 text-carbon-500 hover:text-purple-600"
                            onClick={() => setEditItem(item)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label="Delete"
                            className="h-7 w-7 shrink-0 text-carbon-500 hover:text-red-600"
                            onClick={() => setDeleteItem(item)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-carbon-800 leading-relaxed whitespace-pre-wrap">
                    {item.body}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-carbon-500">
              {data?.total
                ? `Showing ${(currentPage - 1) * INFO_PAGE_SIZE + 1}–${Math.min(
                    currentPage * INFO_PAGE_SIZE,
                    data.total,
                  )} of ${data.total}`
                : ''}
            </p>
            <Pagination
              page={currentPage}
              pageCount={pageCount}
              onPageChange={(page) =>
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  if (page > 1) next.set('infoPage', String(page));
                  else next.delete('infoPage');
                  return next;
                })
              }
            />
          </div>
        </>
      )}

      {/* Create dialog */}
      {createOpen && (
        <CreateInfoTextDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onSuccess={() =>
            toast({ title: 'Informational text created', variant: 'success' })
          }
        />
      )}

      {/* Edit dialog */}
      {editItem && (
        <EditInfoTextDialog
          open={!!editItem}
          onOpenChange={(o) => { if (!o) setEditItem(null); }}
          item={editItem}
        />
      )}

      {/* Delete confirmation */}
      {deleteItem && (
        <DeleteInfoTextDialog
          open={!!deleteItem}
          onOpenChange={(o) => { if (!o) setDeleteItem(null); }}
          item={deleteItem}
        />
      )}
    </div>
  );
}

// -- Create Informational Text Dialog --

function CreateInfoTextDialog({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}) {
  const createMutation = useCreateInformationalText();
  const { data: categories = [] } = useInfoCategories();
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('__none__');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await createMutation.mutateAsync({
        body,
        category: category === '__none__' ? undefined : category,
      });
      onSuccess();
      onOpenChange(false);
    } catch {
      // Error handled by mutation
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New Informational Text</DialogTitle>
          <DialogDescription>
            Create a reusable informational text entry for templates.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <Label className="mb-1">Text</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Enter informational text shown to the patient..."
              className="min-h-[100px]"
              required
            />
          </div>
          <div>
            <Label className="mb-1">Category (optional)</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder="None (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!body.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// -- Edit Informational Text Dialog --

function EditInfoTextDialog({
  open,
  onOpenChange,
  item,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: InformationalText;
}) {
  const { toast } = useToast();
  const updateMutation = useUpdateInformationalText();
  const { data: categories = [] } = useInfoCategories();
  const [body, setBody] = useState(item.body);
  const [category, setCategory] = useState(item.category ?? '__none__');

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    // '__none__' → null (clear). Only send category when it changed; null
    // clears, a string sets, undefined leaves it unchanged.
    const selected = category === '__none__' ? null : category;
    const original = item.category ?? null;
    try {
      await updateMutation.mutateAsync({
        textId: item.informational_text_id,
        data: {
          body: body !== item.body ? body : undefined,
          category: selected !== original ? selected : undefined,
        },
      });
      toast({ title: 'Informational text updated', variant: 'success' });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Update failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Informational Text</DialogTitle>
          <DialogDescription>
            Update the text content and category.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-4 mt-2">
          <div>
            <Label className="mb-1">Text</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="min-h-[100px]"
              required
            />
          </div>
          <div>
            <Label className="mb-1">Category (optional)</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder="None (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!body.trim() || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
